import { Injectable, signal } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type AppType = 'hac' | 'ticketing' | 'client-portal';

@Injectable({
  providedIn: 'root',
})
export class AuthStateService {
  private readonly TOKEN_KEYS = {
    hac: 'hacToken',
    ticketing: 'hnticketing',
    'client-portal': 'clientPortalToken',
  };

  private readonly REFRESH_TOKEN_KEYS = {
    hac: 'hacRefreshtoken',
    ticketing: 'hnRefreshtoken',
    'client-portal': 'clientPortalRefreshToken',
  };

  private readonly HAC_MEMBER_NUMBER_KEY = 'hacMemberNumber';
  private readonly USER_EMAIL_KEY = 'userEmail';

  private appType = signal<AppType | null>(null);
  private currentTokenKey = signal<string>('');
  private currentRefreshTokenKey = signal<string>('');
  
  // Observable to notify subscribers when auth state changes
  private authStateChange$ = new BehaviorSubject<boolean>(this.isAuthenticated());

  /**
   * Initialize the auth state service with the app type
   * This should be called early in the app lifecycle
   */
  public initialize(appType: AppType): void {
    this.appType.set(appType);
    this.currentTokenKey.set(this.TOKEN_KEYS[appType]);
    this.currentRefreshTokenKey.set(this.REFRESH_TOKEN_KEYS[appType]);
  }

  /**
   * Get the current app type
   */
  public getAppType(): AppType | null {
    return this.appType();
  }

  /**
   * Get the token storage key for the current app
   */
  public getTokenKey(): string {
    return this.currentTokenKey();
  }

  /**
   * Get the refresh token storage key for the current app
   */
  public getRefreshTokenKey(): string {
    return this.currentRefreshTokenKey();
  }

  /**
   * Get the stored access token for the current app
   */
  public getToken(): string | null {
    const key = this.getTokenKey();
    return key ? sessionStorage.getItem(key) : null;
  }

  /**
   * Get the stored refresh token for the current app
   */
  public getRefreshToken(): string | null {
    const key = this.getRefreshTokenKey();
    return key ? sessionStorage.getItem(key) : null;
  }

  /**
   * Set the access token for the current app
   */
  public setToken(token: string): void {
    const key = this.getTokenKey();
    if (key) {
      sessionStorage.setItem(key, token);
      this.authStateChange$.next(true);
    }
  }

  /**
   * Set the refresh token for the current app
   */
  public setRefreshToken(refreshToken: string): void {
    const key = this.getRefreshTokenKey();
    if (key) {
      sessionStorage.setItem(key, refreshToken);
    }
  }

  /**
   * Set both access and refresh tokens for the current app
   */
  public setTokens(accessToken: string, refreshToken: string): void {
    this.setToken(accessToken);
    this.setRefreshToken(refreshToken);
  }

  /**
   * Clear all tokens and auth state
   */
  public clearState(): void {
    // Clear all possible app tokens
    Object.values(this.TOKEN_KEYS).forEach(key => sessionStorage.removeItem(key));
    Object.values(this.REFRESH_TOKEN_KEYS).forEach(key => sessionStorage.removeItem(key));
    this.removeUserEmail();
    this.removeHacMemberNumber();
    
    // Reset signals
    this.appType.set(null);
    this.currentTokenKey.set('');
    this.currentRefreshTokenKey.set('');
    
    // Notify subscribers
    this.authStateChange$.next(false);
  }

  /**
   * Remove the access token for the current app
   */
  public removeToken(): void {
    const key = this.getTokenKey();
    if (key) {
      sessionStorage.removeItem(key);
    }
  }

  /**
   * Remove the refresh token for the current app
   */
  public removeRefreshToken(): void {
    const key = this.getRefreshTokenKey();
    if (key) {
      sessionStorage.removeItem(key);
    }
  }

  /**
   * Remove both access and refresh tokens for the current app
   */
  public removeTokens(): void {
    this.removeToken();
    this.removeRefreshToken();
    this.authStateChange$.next(false);
  }

  /**
   * Check if user is authenticated
   */
  public isAuthenticated(): boolean {
    return !!this.getToken();
  }

  /**
   * Get the login route for the current app
   */
  public getLoginRoute(): string {
    const app = this.getAppType();
    return app === 'hac' ? '/login' : '/login';
  }

  /**
   * Set the user email in session storage
   */
  public setUserEmail(email: string): void {
    sessionStorage.setItem(this.USER_EMAIL_KEY, email);
  }

  /**
   * Get the user email from session storage
   */
  public getUserEmail(): string | null {
    return sessionStorage.getItem(this.USER_EMAIL_KEY);
  }

  /**
   * Remove the user email from session storage
   */
  public removeUserEmail(): void {
    sessionStorage.removeItem(this.USER_EMAIL_KEY);
  }

  /**
   * Set the HAC member number in session storage
   */
  public setHacMemberNumber(memberNumber: string): void {
    sessionStorage.setItem(this.HAC_MEMBER_NUMBER_KEY, memberNumber);
  }

  /**
   * Get the HAC member number from session storage
   */
  public getHacMemberNumber(): string | null {
    return sessionStorage.getItem(this.HAC_MEMBER_NUMBER_KEY);
  }

  /**
   * Remove the HAC member number from session storage
   */
  public removeHacMemberNumber(): void {
    sessionStorage.removeItem(this.HAC_MEMBER_NUMBER_KEY);
  }

  /**
   * Get an observable that emits when authentication state changes
   */
  public onAuthStateChange(): Observable<boolean> {
    return this.authStateChange$.asObservable();
  }
}

